import { useMemo, useState } from 'react';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import type { PostPlatform, PublishingAccount } from '../../types/publishing';
import { groupAccountsForMenu } from '../../lib/publishing/accountMenuGrouping';
import type { MenuGroupMode } from '../../lib/publishing/calendarSelection';

// Publishing multi-account — the postable-account selector (2026-07-28). Modelled
// on the broadcast AudiencePanel's grouped tree + select-all, over the POSTABLE
// account set (listPublishingAccounts), selecting accounts, not contacts.
//
// The `groupMode` (2026-07-29) reorganises the menu — Brand headers / owner-Group
// headers / a flat alphabetical list — via the pure groupAccountsForMenu. Default
// 'brand' keeps the post-to-many composer unchanged; the calendar rail passes the
// user's chosen mode. Grouping is DISPLAY-only.

const CONNECTED_ORDER: PostPlatform[] = ['facebook', 'instagram', 'googlebusiness'];

function connectedPlatforms(a: PublishingAccount): PostPlatform[] {
  return CONNECTED_ORDER.filter((p) => a.connected[p]);
}

export function MultiAccountPanel({
  accounts,
  selectedIds,
  onChange,
  groupMode = 'brand',
}: {
  accounts: PublishingAccount[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  groupMode?: MenuGroupMode;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.brandName ?? '').toLowerCase().includes(q) ||
        (a.groupName ?? '').toLowerCase().includes(q),
    );
  }, [accounts, search]);

  const groups = useMemo(() => groupAccountsForMenu(filtered, groupMode), [filtered, groupMode]);

  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const setMany = (ids: string[], on: boolean) => {
    const next = new Set(selectedIds);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    onChange(next);
  };

  const allIds = filtered.map((a) => a.id);
  const allOn = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium text-slate-700">
          Accounts{' '}
          <span className="font-normal text-slate-500">({selectedCount} selected)</span>
        </label>
        <TriCheckbox
          checked={allOn}
          indeterminate={!allOn && allIds.some((id) => selectedIds.has(id))}
          onChange={() => setMany(allIds, !allOn)}
          label="Select all"
        />
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by account, brand or group…"
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
      />

      <div className="scroll-subtle max-h-[28rem] space-y-3 overflow-y-auto rounded-lg border border-slate-200 p-2">
        {allIds.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-slate-400">No postable accounts match.</p>
        ) : (
          groups.map((group) => {
            const ids = group.accounts.map((a) => a.id);
            const groupOn = ids.every((id) => selectedIds.has(id));
            const groupSome = !groupOn && ids.some((id) => selectedIds.has(id));
            return (
              <div key={group.key}>
                {/* Header only for grouped modes; the flat list has no header. */}
                {group.label !== null && (
                  <div className="mb-1 flex items-center gap-2 border-b border-slate-100 pb-1">
                    <TriCheckbox
                      checked={groupOn}
                      indeterminate={groupSome}
                      onChange={() => setMany(ids, !groupOn)}
                    />
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {group.label}
                    </span>
                    <span className="text-xs text-slate-400">({group.accounts.length})</span>
                  </div>
                )}
                <ul>
                  {group.accounts.map((a) => {
                    const on = selectedIds.has(a.id);
                    return (
                      <li key={a.id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-slate-50">
                          <input type="checkbox" checked={on} onChange={() => toggle(a.id)} />
                          <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{a.name}</span>
                          <span className="flex shrink-0 items-center gap-1 text-slate-400">
                            {connectedPlatforms(a).map((p) => (
                              <ChannelIcon key={p} channel={p} className="h-3.5 w-3.5" />
                            ))}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  label?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
      <input
        type="checkbox"
        checked={checked}
        ref={(el) => {
          if (el) el.indeterminate = indeterminate;
        }}
        onChange={onChange}
      />
      {label}
    </label>
  );
}
